import { z } from 'zod';
import type { BooleanFlag, ModuleKey } from '../enums';
import { idSchema, localDateSchema, markdownSchema, nameSchema, rateSchema, urlSchema } from './common';
import { COMPANY_REGISTRY_FIELDS } from './company-registry';
import { CLIENT_EDITABLE_STATUSES, clientLinkSchema } from './crm';
import { idempotencyKeySchema, mcpContactVersionArg, mcpNoteArg } from './mcp-base';
import { PROJECT_MEMBER_ROLES, PROJECT_STATUSES } from './projects';
import { TASK_STATUSES } from './work';

/**
 * Pełny MCP: klienci, projekty, zadania i czas (DECISIONS 2026-10-09 [S] „Claude może przez konektor to samo co
 * Sebastian w pracy z klientami (pełny MCP)”). Odnośniki i opiekun klienta, przywrócenie z archiwum, kontakt główny,
 * archiwum kontaktu, dane firmy z rejestru po NIP; edycja, archiwum i osoby projektu, stawka partnera; przeniesienie
 * i kolejność zadania, checklista, archiwum zadania; archiwum wpisu czasu.
 *
 * Cienkie adaptery na usługi aplikacji (`packages/core/src/mcp/tools/crm-projects-tasks.ts`): uprawnienia osoby ∩
 * zakres tokenu, mutacje tylko z tokenem `write`, `idempotency_key` w każdej mutacji, `expected_version` przy edycji
 * rekordu z wersją. Nic nie jest usuwane na zawsze (archiwum i przywrócenie). Bez sejfu, ustawień, zespołu, integracji
 * i tokenów. Rejestr narzędzi: `MCP_CRM_PROJECTS_TASKS_TOOLS` (wpisy w formacie `MCP_TOOLS` z `contracts/mcp.ts`).
 */

const versionArg = z.number().int().positive();
const mutationTail = { note: mcpNoteArg, idempotency_key: idempotencyKeySchema };

/** Osoba z workspace'u wskazana emailem albo imieniem i nazwiskiem (dokładnie, bez wielkości liter). */
const personArg = z.string().trim().min(2).max(200);

/** Dokładnie jedno z: `user_id` / `person` (opcjonalnie z `null` = „bez osoby”). */
function onePerson(idKey: string, personKey: string, allowNull: boolean) {
  return (v: Record<string, unknown>, ctx: z.RefinementCtx) => {
    const hasId = v[idKey] !== undefined && (allowNull || v[idKey] !== null);
    const hasPerson = v[personKey] !== undefined;
    if (hasId === hasPerson) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [idKey],
        message: `Podaj ${idKey} albo ${personKey} (dokładnie jedno)`,
      });
    }
  };
}

const atLeastOne = (v: Record<string, unknown>) =>
  Object.entries(v).some(
    ([k, val]) =>
      !['note', 'idempotency_key', 'expected_version'].includes(k) &&
      !k.endsWith('_id') &&
      val !== undefined &&
      (!Array.isArray(val) || val.length > 0),
  );

// ── Klienci ─────────────────────────────────────────────────────────────────

const linkLabelArg = clientLinkSchema.shape.label;

export const updateClientLinksArgs = z
  .object({
    client_id: idSchema,
    /** `version` z get_client (inna = CONFLICT) */
    expected_version: versionArg,
    /** nowe odnośniki (na końcu listy) */
    add: z.array(clientLinkSchema.strict()).max(20).optional(),
    /** zmiana odnośnika wskazanego bieżącym `url`: nowa etykieta i / albo nowy adres */
    update: z
      .array(
        z
          .object({ url: urlSchema, label: linkLabelArg.optional(), new_url: urlSchema.optional() })
          .strict()
          .refine((u) => u.label !== undefined || u.new_url !== undefined, { message: 'Podaj label albo new_url' }),
      )
      .max(20)
      .optional(),
    /** usuń z listy odnośniki o tych adresach */
    remove: z.array(urlSchema).max(20).optional(),
    ...mutationTail,
  })
  .strict()
  .refine(atLeastOne, { message: 'Podaj add, update albo remove' });

export const setClientOwnerArgs = z
  .object({
    client_id: idSchema,
    expected_version: versionArg,
    /** id osoby; `null` = bez opiekuna */
    owner_user_id: idSchema.nullable().optional(),
    /** albo osoba z workspace'u: email lub imię i nazwisko */
    owner: personArg.optional(),
    ...mutationTail,
  })
  .strict()
  .superRefine(onePerson('owner_user_id', 'owner', true));

export const restoreClientArgs = z
  .object({
    client_id: idSchema,
    expected_version: versionArg,
    /** status po przywróceniu (domyślnie „Aktywny”) */
    status: z.enum(CLIENT_EDITABLE_STATUSES).default('active'),
    ...mutationTail,
  })
  .strict();

/** Kontakt: kontakt główny, archiwum, przywrócenie. */
export const contactVersionArgs = z
  .object({ contact_id: idSchema, expected_version: mcpContactVersionArg, ...mutationTail })
  .strict();

export const refreshClientRegistryArgs = z
  .object({
    client_id: idSchema,
    expected_version: versionArg,
    /** pola do nadpisania danymi z rejestru (domyślnie wszystkie) */
    fields: z.array(z.enum(COMPANY_REGISTRY_FIELDS)).min(1).max(COMPANY_REGISTRY_FIELDS.length).optional(),
    ...mutationTail,
  })
  .strict();

// ── Projekty ────────────────────────────────────────────────────────────────

export const updateProjectArgs = z
  .object({
    project_id: idSchema,
    /** `version` z get_project (inna = CONFLICT) */
    expected_version: versionArg,
    changes: z
      .object({
        name: nameSchema.optional(),
        status: z.enum(PROJECT_STATUSES).optional(),
        start_date: localDateSchema.nullable().optional(),
        end_date: localDateSchema.nullable().optional(),
        goal: z.string().trim().max(2000).nullable().optional(),
        scope_md: markdownSchema.nullable().optional(),
        /** budżet godzin (np. 40); `null` = bez budżetu */
        hours_budget: z.number().positive().max(100_000).nullable().optional(),
        figma_url: urlSchema.nullable().optional(),
        repo_url: urlSchema.nullable().optional(),
        /** domyślnie rozliczany czas w projekcie */
        billable_default: z.boolean().optional(),
      })
      .strict()
      .refine((c) => Object.values(c).some((v) => v !== undefined), { message: 'Podaj co najmniej jedną zmianę' })
      .refine((c) => !(c.start_date && c.end_date && c.end_date < c.start_date), {
        message: 'Koniec przed początkiem',
        path: ['end_date'],
      }),
    ...mutationTail,
  })
  .strict();

export const projectVersionArgs = z
  .object({ project_id: idSchema, expected_version: versionArg, ...mutationTail })
  .strict();

const projectPersonShape = {
  project_id: idSchema,
  /** id osoby (np. z get_project → members) */
  user_id: idSchema.optional(),
  /** albo osoba z workspace'u: email lub imię i nazwisko */
  person: personArg.optional(),
};

export const addProjectPersonArgs = z
  .object({
    ...projectPersonShape,
    /** lead = prowadzący (poprzedni prowadzący zostaje członkiem) */
    role: z.enum(PROJECT_MEMBER_ROLES).default('member'),
    ...mutationTail,
  })
  .strict()
  .superRefine(onePerson('user_id', 'person', false));

export const removeProjectPersonArgs = z
  .object({ ...projectPersonShape, ...mutationTail })
  .strict()
  .superRefine(onePerson('user_id', 'person', false));

export const setProjectPartnerRateArgs = z
  .object({
    ...projectPersonShape,
    /** stawka partnera netto/h w projekcie, np. „120”; `null` = domyślna stawka osoby */
    partner_rate: rateSchema.nullable(),
    ...mutationTail,
  })
  .strict()
  .superRefine(onePerson('user_id', 'person', false));

// ── Zadania ─────────────────────────────────────────────────────────────────

export const moveTaskArgs = z
  .object({
    task_id: idSchema,
    expected_version: versionArg,
    /** kolumna (status) docelowa — może być ta sama, gdy zmieniasz tylko kolejność */
    status: z.enum(TASK_STATUSES),
    /** zadanie, za którym ląduje karta; pominięte / `null` = na górze kolumny */
    after_task_id: idSchema
      .nullable()
      .default(null)
      .describe('Zadanie, za którym ląduje karta (id z list_tasks); null albo brak = na górze kolumny'),
    ...mutationTail,
  })
  .strict();

export const updateTaskChecklistArgs = z
  .object({
    task_id: idSchema,
    /** `version` z list_tasks (inna = CONFLICT) */
    expected_version: versionArg,
    add: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
    done: z.array(idSchema).max(50).optional(),
    undone: z.array(idSchema).max(50).optional(),
    rename: z
      .array(z.object({ item_id: idSchema, text: z.string().trim().min(1).max(500) }).strict())
      .max(50)
      .optional(),
    /** usuń punkty checklisty (punkt, nie zadanie) */
    remove: z.array(idSchema).max(50).optional(),
    ...mutationTail,
  })
  .strict()
  .refine(atLeastOne, { message: 'Podaj add, done, undone, rename albo remove' });

export const taskVersionArgs = z.object({ task_id: idSchema, expected_version: versionArg, ...mutationTail }).strict();

// ── Czas ────────────────────────────────────────────────────────────────────

export const timeEntryVersionArgs = z
  .object({ entry_id: idSchema, expected_version: versionArg, ...mutationTail })
  .strict();

// ── Wyniki (data w kopercie) ────────────────────────────────────────────────

/** Osoba projektu (`add_project_person`, `remove_project_person`, `set_project_partner_rate`). */
export interface McpProjectPerson {
  id: string;
  name: string;
  role: string;
  /** dostęp do workspace'u wyłączony (historia zostaje) */
  disabled: boolean;
  /** stawka partnera w projekcie i domyślna — tylko owner / admin / manager i sama osoba (jak w aplikacji) */
  partner_rate?: string | null;
  default_partner_rate?: string | null;
}

/** Wynik kroku rejestru w `refresh_client_registry`. */
export interface McpRegistryRefresh {
  source: string;
  simulated: boolean;
  fields: string[];
  checked_at: string | null;
}

// ── Rejestr narzędzi tego obszaru ───────────────────────────────────────────

/** Wpis rejestru w formacie `McpToolSpec` z `contracts/mcp.ts` (kopia typu: bez cyklu importów). */
export interface McpCrmToolSpec<N extends string = string> {
  name: N;
  version: number;
  title: string;
  description: string;
  mutating: boolean;
  createsApproval: boolean;
  module: ModuleKey | null;
  level: 'view' | 'edit';
  flags: readonly BooleanFlag[];
  runToken: boolean;
  input: z.ZodTypeAny;
}

function tool<const N extends string>(spec: McpCrmToolSpec<N>): McpCrmToolSpec<N> {
  return spec;
}

const edit = { version: 1, mutating: true, createsApproval: false, level: 'edit', flags: [] } as const;

export const MCP_CRM_PROJECTS_TASKS_TOOLS = [
  // Klienci
  tool({
    ...edit,
    name: 'update_client_links',
    title: 'Odnośniki klienta',
    description:
      'Dodaje, zmienia albo usuwa odnośniki klienta (etykieta + adres, np. strona, Figma, panel). Zmianę wskazujesz bieżącym url. Wymaga expected_version z get_client.',
    module: 'clients',
    runToken: false,
    input: updateClientLinksArgs,
  }),
  tool({
    ...edit,
    name: 'set_client_owner',
    title: 'Zmień opiekuna klienta',
    description:
      'Ustawia opiekuna klienta (owner_user_id albo owner: email / imię i nazwisko; null = bez opiekuna). Tylko gdy osoba może to zrobić w aplikacji. Używaj na wyraźne polecenie — opiekun daje dostęp do klienta.',
    module: 'clients',
    runToken: false,
    input: setClientOwnerArgs,
  }),
  tool({
    ...edit,
    name: 'restore_client',
    title: 'Przywróć klienta',
    description:
      'Przywraca klienta z archiwum (status domyślnie „Aktywny”; klienta znajdziesz przez search_clients ze status: archived). Archiwizacja klienta tylko przez propose_client_archive.',
    module: 'clients',
    runToken: false,
    input: restoreClientArgs,
  }),
  tool({
    ...edit,
    name: 'set_primary_contact',
    title: 'Kontakt główny',
    description:
      'Ustawia kontakt jako główny kontakt klienta (poprzedni przestaje być główny). expected_version = version kontaktu z get_client → contacts[] (nie wersja klienta).',
    module: 'clients',
    runToken: false,
    input: contactVersionArgs,
  }),
  tool({
    ...edit,
    name: 'archive_contact',
    title: 'Archiwizuj kontakt',
    description:
      'Przenosi kontakt klienta do archiwum (nie usuwa; restore_contact przywraca). Używaj, gdy osoba już nie pracuje u klienta. expected_version = version kontaktu z get_client → contacts[] (nie wersja klienta).',
    module: 'clients',
    runToken: false,
    input: contactVersionArgs,
  }),
  tool({
    ...edit,
    name: 'restore_contact',
    title: 'Przywróć kontakt',
    description:
      'Przywraca kontakt klienta z archiwum. contact_id i version: get_client z include_archived: true → contacts[] (archived: true).',
    module: 'clients',
    runToken: false,
    input: contactVersionArgs,
  }),
  tool({
    ...edit,
    name: 'refresh_client_registry',
    title: 'Dane firmy z rejestru',
    description:
      'Odświeża dane firmy klienta z rejestru po jego NIP (nazwa prawna, REGON, KRS, VAT, adres; fields = wybrane pola). Wartości bierze serwer z rejestru, nie z argumentów. Klient musi mieć NIP.',
    module: 'clients',
    runToken: false,
    input: refreshClientRegistryArgs,
  }),
  // Projekty
  tool({
    ...edit,
    name: 'update_project',
    title: 'Zmień projekt',
    description:
      'Zmienia projekt: nazwa, status, daty, cel, zakres, budżet godzin, Figma, repozytorium, rozliczany domyślnie. Wymaga expected_version z get_project. Budżet i rozliczanie tylko z dostępem do całego klienta.',
    module: 'projects',
    runToken: false,
    input: updateProjectArgs,
  }),
  tool({
    ...edit,
    name: 'archive_project',
    title: 'Archiwizuj projekt',
    description: 'Przenosi projekt do archiwum (tylko do odczytu, nic nie jest usuwane; restore_project przywraca).',
    module: 'projects',
    runToken: false,
    input: projectVersionArgs,
  }),
  tool({
    ...edit,
    name: 'restore_project',
    title: 'Przywróć projekt',
    description: 'Przywraca projekt z archiwum (project_id: list_projects z view: closed; version: get_project).',
    module: 'projects',
    runToken: false,
    input: projectVersionArgs,
  }),
  tool({
    ...edit,
    name: 'add_project_person',
    title: 'Dodaj osobę do projektu',
    description:
      'Dodaje osobę z workspace’u do projektu (user_id albo person: email / imię i nazwisko; role lead = prowadzący). Tylko gdy osoba tokenu może zmieniać członków w aplikacji. Daje dostęp do projektu — na wyraźne polecenie.',
    module: 'projects',
    runToken: false,
    input: addProjectPersonArgs,
  }),
  tool({
    ...edit,
    name: 'remove_project_person',
    title: 'Usuń osobę z projektu',
    description:
      'Zdejmuje osobę z projektu (jej wpisy i zadania zostają). Tylko gdy osoba tokenu może zmieniać członków w aplikacji.',
    module: 'projects',
    runToken: false,
    input: removeProjectPersonArgs,
  }),
  tool({
    ...edit,
    name: 'set_project_partner_rate',
    title: 'Stawka partnera w projekcie',
    description:
      'Ustawia stawkę partnera osoby w projekcie (null = jej domyślna). Tylko owner, admin albo manager (manager nie zmienia własnej), jak w aplikacji. Obejmuje nierozliczone wpisy osoby w projekcie.',
    module: 'projects',
    runToken: false,
    input: setProjectPartnerRateArgs,
  }),
  // Zadania
  tool({
    ...edit,
    name: 'move_task',
    title: 'Przenieś zadanie',
    description:
      'Przenosi zadanie na tablicy: kolumna (status) i miejsce (after_task_id; null = na górze). Do zmiany kolejności w tej samej kolumnie podaj ten sam status. expected_version z list_tasks.',
    module: 'tasks',
    runToken: true,
    input: moveTaskArgs,
  }),
  tool({
    ...edit,
    name: 'update_task_checklist',
    title: 'Checklista zadania',
    description:
      'Zmienia checklistę zadania: dodaj punkty, odhacz / cofnij, zmień tekst, usuń punkt (id punktów z list_tasks z task_id). expected_version z list_tasks.',
    module: 'tasks',
    runToken: true,
    input: updateTaskChecklistArgs,
  }),
  tool({
    ...edit,
    name: 'archive_task',
    title: 'Archiwizuj zadanie',
    description:
      'Przenosi zadanie do archiwum (nic nie jest usuwane; restore_task przywraca). expected_version z list_tasks.',
    module: 'tasks',
    runToken: false,
    input: taskVersionArgs,
  }),
  tool({
    ...edit,
    name: 'restore_task',
    title: 'Przywróć zadanie',
    description:
      'Przywraca zadanie z archiwum (na koniec swojej kolumny). task_id i version: list_tasks z archived: true.',
    module: 'tasks',
    runToken: false,
    input: taskVersionArgs,
  }),
  // Czas
  tool({
    ...edit,
    name: 'archive_time_entry',
    title: 'Archiwizuj wpis czasu',
    description:
      'Przenosi wpis czasu do archiwum zamiast usuwania („Usuń” w aplikacji): własny, poza zestawieniem i rozliczeniem partnera (manager / admin także cudze). restore_time_entry cofa.',
    module: 'time',
    runToken: false,
    input: timeEntryVersionArgs,
  }),
  tool({
    ...edit,
    name: 'restore_time_entry',
    title: 'Przywróć wpis czasu',
    description: 'Przywraca wpis czasu z archiwum. entry_id i version: list_time_entries z archived: true.',
    module: 'time',
    runToken: false,
    input: timeEntryVersionArgs,
  }),
] as const;

export type McpCrmProjectsTasksToolName = (typeof MCP_CRM_PROJECTS_TASKS_TOOLS)[number]['name'];
